/**
 * Add Product Configuration
 * Uses custom submit handler to support pallet/case/unit quantities.
 * total_base_units is a GENERATED column in DB, so we insert the breakdown fields.
 */

import { supabase } from '../../../../../supabaseClient';

const BUCKET_MAP = {
  invoice:        'products-inventory-invoice-documents',
  po:             'products-inventory-po',
  coa:            'products-inventory-coa-documents',
  sds:            'products-inventory-sds-documents',
  bol:            'products-transactions-bol',
  production_doc: 'products-inventory-production-documents'
};

async function uploadProductDocuments(companyId, documents) {
  const uploads = {};
  const timestamp = Date.now();

  for (const [key, bucket] of Object.entries(BUCKET_MAP)) {
    const file = documents?.[key];
    if (!file) continue;
    try {
      const safeName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
      const filePath = `${companyId}/${timestamp}_${safeName}`;
      const { error } = await supabase.storage
        .from(bucket)
        .upload(filePath, file, { cacheControl: '3600', upsert: false });
      if (!error) {
        uploads[key] = { path: filePath, name: file.name, size: file.size };
      } else {
        console.error(`Upload error for ${key}:`, error);
      }
    } catch (err) {
      console.error(`Upload exception for ${key}:`, err);
    }
  }
  return uploads;
}

/**
 * Add stock for a product.
 * Accepts palletQty, caseQty, unitQty — DB calculates total_base_units via generated column.
 */
export async function submitAddStockProduct(companyId, inventoryType, config, data) {
  try {
    const {
      item, location, quantity,
      lotNumber, batchNumber,
      vendor, invoiceNumber, invoiceDate,
      poNumber, poDate, unitCost,
      palletQty, caseQty, unitQty,
      mfgDate, expiryDate, shelfLife,
      variantId, unitConfigId,
      documents, notes
    } = data;

    if (!item || !location) {
      throw new Error('Product and location are required');
    }
    if (!lotNumber || !batchNumber) {
      throw new Error('Lot number and batch number are required');
    }

    // Fetch unit configuration for this product
    let unitConfig = null;
    if (unitConfigId) {
      const { data: ucData } = await supabase
        .from('unit_configurations')
        .select('*')
        .eq('id', unitConfigId)
        .single();
      unitConfig = ucData;
    }
    // Fallback: try to find default config for this product
    if (!unitConfig) {
      const { data: ucData } = await supabase
        .from('unit_configurations')
        .select('*')
        .eq('product_id', item.id)
        .eq('company_id', companyId)
        .limit(1)
        .single();
      unitConfig = ucData;
    }

    const unitsPerCase   = unitConfig?.units_per_case || 1;
    const casesPerPallet = unitConfig?.cases_per_pallet || 1;
    const enableCases    = unitConfig?.enable_cases ?? true;
    const enablePallets  = unitConfig?.enable_pallets ?? false;

    // Calculate breakdown quantities
    const pQty = parseInt(palletQty) || 0;
    const cQty = parseInt(caseQty)   || 0;
    const uQty = parseInt(unitQty)   || parseInt(quantity) || 0;

    // Compute total_base_units for the transaction
    const totalBaseUnits = (pQty * casesPerPallet * unitsPerCase) + (cQty * unitsPerCase) + uQty;

    if (totalBaseUnits <= 0) {
      throw new Error('Total quantity must be greater than 0');
    }

    const finalCost = unitCost ? parseFloat(unitCost) : null;

    // Upload documents
    const docUploads = await uploadProductDocuments(companyId, documents || {});

    // Build transaction record
    const transactionData = {
      company_id:       companyId,
      product_id:       item.id,
      variant_id:       variantId || null,
      to_location_id:   location.id,
      transaction_type: 'inventory_creation',
      quantity_change:  totalBaseUnits,
      input_pallets:    pQty,
      input_cases:      cQty,
      input_units:      uQty,
      lot_number:       lotNumber,
      batch_number:     batchNumber,
      manufactured_date: mfgDate || null,
      expiry_date:       expiryDate || null,
      supplier_id:      vendor?.id || null,
      unit_cost:        finalCost,
      invoice_number:   invoiceNumber || null,
      invoice_date:     invoiceDate || null,
      po_number:        poNumber || null,
      po_date:          poDate || null,
      units_per_case:   unitsPerCase,
      cases_per_pallet: casesPerPallet,
      notes:            notes || null,
      created_at:       new Date().toISOString()
    };

    // Add document paths
    if (docUploads.invoice) {
      transactionData.invoice_file_path = docUploads.invoice.path;
      transactionData.invoice_file_name = docUploads.invoice.name;
    }
    if (docUploads.coa) {
      transactionData.coa_file_path = docUploads.coa.path;
      transactionData.coa_file_name = docUploads.coa.name;
    }
    if (docUploads.sds) {
      transactionData.sds_file_path = docUploads.sds.path;
      transactionData.sds_file_name = docUploads.sds.name;
    }
    if (docUploads.po) {
      transactionData.po_file_path = docUploads.po.path;
      transactionData.po_file_name = docUploads.po.name;
    }
    if (docUploads.bol) {
      transactionData.bol_file_path = docUploads.bol.path;
      transactionData.bol_file_name = docUploads.bol.name;
    }
    // Note: production_doc columns only exist on items table, not transactions

    const { data: txData, error: txError } = await supabase
      .from('product_inventory_transactions')
      .insert(transactionData)
      .select()
      .single();

    if (txError) throw txError;

    // Create inventory item (lot record)
    // Note: total_base_units is a GENERATED column — do NOT insert it
    const itemData = {
      company_id:            companyId,
      product_id:            item.id,
      variant_id:            variantId || null,
      location_id:           location.id,
      pallet_quantity:        pQty,
      case_quantity:          cQty,
      unit_quantity:          uQty,
      original_pallet_quantity: pQty,
      original_case_quantity:   cQty,
      original_unit_quantity:   uQty,
      units_per_case:        unitsPerCase,
      cases_per_pallet:      casesPerPallet,
      enable_cases:          enableCases,
      enable_pallets:        enablePallets,
      lot_number:            lotNumber,
      batch_number:          batchNumber,
      manufactured_date:     mfgDate || null,
      expiry_date:           expiryDate || null,
      shelf_life_months:     shelfLife ? parseInt(shelfLife) : null,
      source_transaction_id: txData.id,
      is_active:             true,
      last_updated_at:       new Date().toISOString()
    };

    // Add document paths to lot item too
    if (docUploads.invoice) {
      itemData.invoice_file_path = docUploads.invoice.path;
      itemData.invoice_file_name = docUploads.invoice.name;
    }
    if (docUploads.po) {
      itemData.po_file_path = docUploads.po.path;
      itemData.po_file_name = docUploads.po.name;
    }
    if (docUploads.coa) {
      itemData.coa_file_path = docUploads.coa.path;
      itemData.coa_file_name = docUploads.coa.name;
    }
    if (docUploads.sds) {
      itemData.sds_file_path = docUploads.sds.path;
      itemData.sds_file_name = docUploads.sds.name;
    }
    if (docUploads.production_doc) {
      itemData.production_doc_file_path = docUploads.production_doc.path;
      itemData.production_doc_file_name = docUploads.production_doc.name;
    }

    const { error: itemError } = await supabase
      .from('product_inventory_items')
      .insert(itemData);

    if (itemError) throw itemError;

    // Recalculate aggregate
    await supabase.rpc('recalculate_product_inventory_aggregate', {
      p_company_id:  companyId,
      p_product_id:  item.id,
      p_variant_id:  variantId || null,
      p_location_id: location.id
    });

    return { success: true };
  } catch (error) {
    console.error('Error adding product stock:', error);
    return { success: false, error: error.message };
  }
}

export const addProductConfig = {
  type:              'products',
  displayName:       'Products',
  tableName:         'products',
  inventoryTable:    'product_inventory',
  itemsTable:        'product_inventory_items',
  transactionsTable: 'product_inventory_transactions',
  itemIdField:       'product_id',

  // Override the generic submitAddStock
  customSubmitAdd: submitAddStockProduct,

  formFields: [
    { name: 'product_id',     label: 'Product',       type: 'select',   required: true,  section: 'basic' },
    { name: 'location_id',    label: 'Location',      type: 'select',   required: true,  section: 'basic' },
    { name: 'palletQty',      label: 'Pallets',       type: 'number',   required: false, min: 0, step: 1, section: 'basic' },
    { name: 'caseQty',        label: 'Cases',         type: 'number',   required: false, min: 0, step: 1, section: 'basic' },
    { name: 'unitQty',        label: 'Units',         type: 'number',   required: false, min: 0, step: 1, section: 'basic' },
    { name: 'lot_number',     label: 'Lot Number',    type: 'text',     required: true,  section: 'lot' },
    { name: 'batch_number',   label: 'Batch Number',  type: 'text',     required: true,  section: 'lot' },
    { name: 'notes',          label: 'Notes',         type: 'textarea', required: false, rows: 3, section: 'additional' }
  ],

  validationRules: {}
};

export default addProductConfig;
